<script setup lang="ts">
import { ref } from 'vue'
import { CheckCircle2, XCircle, Clock, RefreshCw } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { API, HOST } from '@/utils/http'

const props = defineProps<{
  kycStatus: 'approved' | 'rejected' | 'in_progress' | 'awaiting_ubo'
  rawKycStatus: string
  tosStatus: string
  customerId: number
  bridgeCustomerId: string | null
  label?: string
  isSuperUser?: boolean
}>()

const emit = defineEmits<{
  (e: 'synced'): void
}>()

const syncing = ref(false)

const sync = async (event: Event) => {
  event.stopPropagation()
  syncing.value = true
  try {
    await API.post(`${HOST}/payments/bridge/customers/${props.customerId}/sync`)
    emit('synced')
  } catch (err) {
    console.error('Failed to sync customer status:', err)
  } finally {
    syncing.value = false
  }
}
</script>

<template>
  <div class="inline-flex items-center gap-1.5">
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger as-child>
          <span class="inline-flex cursor-default">
            <Badge
              v-if="kycStatus === 'in_progress' || kycStatus === 'awaiting_ubo'"
              class="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100"
            >
              <Clock class="h-3 w-3 mr-1" />
              {{ rawKycStatus }}
            </Badge>
            <Badge
              v-else-if="kycStatus === 'approved'"
              class="bg-green-100 text-green-700 border-green-200 hover:bg-green-100"
            >
              <CheckCircle2 class="h-3 w-3 mr-1" />
              Verified
            </Badge>
            <Badge
              v-else-if="kycStatus === 'rejected'"
              class="bg-red-100 text-red-700 border-red-200 hover:bg-red-100"
            >
              <XCircle class="h-3 w-3 mr-1" />
              Rejected
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div class="text-xs space-y-1">
            <div>ToS: {{ tosStatus || '-' }}</div>
            <div>{{ label ?? 'KYC' }}: {{ rawKycStatus || '-' }}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
    <button
      v-if="isSuperUser && kycStatus !== 'approved' && bridgeCustomerId"
      @click="sync"
      :disabled="syncing"
      class="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
      title="Refresh status from Bridge"
    >
      <RefreshCw :class="['h-3.5 w-3.5', syncing && 'animate-spin']" />
    </button>
  </div>
</template>
